import { Button } from '@/components/ui/button'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  FormField,
  FormGroup,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import dayjs from 'dayjs'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import TeamMemberLink from 'app/frontend/base/components/TeamMemberLink'
import CheckboxField from 'app/frontend/base/components/form-fields/CheckboxField'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import TextField from 'app/frontend/base/components/form-fields/TextField'
import { AppContext } from 'app/frontend/base/config/context/app'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo } from 'app/frontend/base/config/queries'
import {
  difficulties,
  difficultiesLabels,
} from 'app/frontend/types/custom/Difficulty'
import { characterRoles } from 'app/frontend/types/custom/CharacterRole'
import { getTeamOverviewEvents } from 'app/frontend/base/api/event'
import { useContext, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { DateRange } from 'react-day-picker'
import { z } from 'zod'
import useBulkUserEventStatusMutation from './useBulkUserEventStatusMutation'
import EventParticipantStatus from 'app/frontend/types/custom/EventParticipantStatus'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getSelectableRolesForCharacter } from '../../TeamEvent/components/viewerSignupShared'
import useBulkStatusPreviewEvents, {
  BulkStatusPreviewOptionality,
} from '../utils/useBulkStatusPreviewEvents'
import Card from 'app/frontend/base/components/Card'
import { cn } from '@/lib/utils'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const optionalityOptions: {
  value: BulkStatusPreviewOptionality
  label: string
}[] = [
  { value: 'any', label: 'Any' },
  { value: 'only_optional', label: 'Only optional' },
  { value: 'non_optional', label: 'Non-optional' },
]

const weekDayOptions = [
  { value: '1', label: 'Mondays' },
  { value: '2', label: 'Tuesdays' },
  { value: '3', label: 'Wednesdays' },
  { value: '4', label: 'Thursdays' },
  { value: '5', label: 'Fridays' },
  { value: '6', label: 'Saturdays' },
  { value: '0', label: 'Sundays' },
]

const getBulkSetStatusFormSchema = (
  availableStatuses: EventParticipantStatus[],
) =>
  z
    .object({
      dateRange: z
        .object({
          from: z.date().optional(),
          to: z.date().optional(),
        })
        .optional(),
      weekDay: z.string().nullable(),
      status: z.string().min(1, 'Please select a status'),
      comment: z.string(),
      difficulty: z.enum(difficulties).nullable(),
      optionality: z.enum(['any', 'only_optional', 'non_optional']),
      applyToAllTeams: z.boolean(),
      characterId: z.string().nullable(),
      autoSignupOnly: z.boolean(),
      role: z.enum(characterRoles).nullable(),
    })
    .superRefine((values, context) => {
      if (!values.dateRange?.from || !values.dateRange.to) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dateRange'],
          message: 'Please select both dates',
        })
      } else if (
        dayjs(values.dateRange.to).isBefore(dayjs(values.dateRange.from), 'day')
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dateRange'],
          message: 'Until date must be on or after from date',
        })
      }

      if (
        values.status &&
        !availableStatuses.includes(values.status as EventParticipantStatus)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['status'],
          message: 'Please select a valid status',
        })
      }

      if (values.applyToAllTeams && !values.autoSignupOnly) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['autoSignupOnly'],
          message:
            'Apply to all teams requires auto-signup-only updates to be enabled',
        })
      }

      if (!values.autoSignupOnly && !values.characterId) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['characterId'],
          message: 'Please select a character',
        })
      }

      if (!values.autoSignupOnly && !values.role) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['role'],
          message: 'Please select a role',
        })
      }
    })

type BulkSetStatusFormValues = z.infer<
  ReturnType<typeof getBulkSetStatusFormSchema>
>
type BulkSetStatusFormEntity = BulkSetStatusFormValues & { id?: number }

const BulkSetStatusDialogFields = ({
  availableStatuses,
  selectableCharacters,
  teamMembershipByCharacterId,
  currentCharacterId,
  dayEvents,
}: {
  availableStatuses: EventParticipantStatus[]
  selectableCharacters: wowaudit.CharacterReference[]
  teamMembershipByCharacterId: Map<number, wowaudit.TeamMembership>
  currentCharacterId?: number
  dayEvents: wowaudit.EventReference[]
}) => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()
  const form = useFormContext<BulkSetStatusFormValues>()
  const applyToAllTeams = form.watch('applyToAllTeams')
  const autoSignupOnly = form.watch('autoSignupOnly')
  const selectedCharacterId = form.watch('characterId')
  const selectedCharacter = selectedCharacterId
    ? selectableCharacters.find(
        (character) => character.id === Number(selectedCharacterId),
      )
    : undefined
  const availableRoles = selectedCharacter
    ? getSelectableRolesForCharacter(selectedCharacter.classId)
    : characterRoles

  return (
    <div>
      <FormGroup>
        <FormField
          control={form.control}
          name='dateRange'
          render={({ field }) => (
            <FormItem className='w-full space-y-2'>
              <FormLabel>Date range</FormLabel>
              <DateRangePicker
                date={field.value as DateRange | undefined}
                setDate={(value) => field.onChange(value)}
                placeholder='Set dates'
                minDate={new Date()}
                maxDate={dayjs().add(3, 'month').toDate()}
                dayEvents={dayEvents}
                currentUserId={user?.id}
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <div className='w-full'>
          <SelectField
            name='weekDay'
            options={weekDayOptions}
            transformOptions={false}
            customLabel='Weekday'
            customPlaceholder='All weekdays'
            isClearable
            inModal
          />
        </div>

        <div className='w-full'>
          <SelectField
            name='status'
            options={availableStatuses.map((value) => ({
              value,
              label: (
                <span className='flex items-center gap-1.5'>
                  <EventStatusIcon status={value} size='small' />
                  {t(`settings.raidsAvailableStatuses.options.${value}`)}
                </span>
              ),
            }))}
            transformOptions={false}
            customLabel='New status'
            customPlaceholder='Select status'
            inModal
          />
        </div>
      </FormGroup>

      <TextField
        name='comment'
        customLabel='Comment'
        customPlaceholder='Comment'
      />

      <Accordion type='single' collapsible>
        <AccordionItem value='advanced-options'>
          <AccordionTrigger className='text-sm py-0'>
            Advanced options
          </AccordionTrigger>
          <AccordionContent>
            <FormGroup>
              <SelectField
                name='difficulty'
                options={difficulties.map((value) => ({
                  value,
                  label: difficultiesLabels[value],
                }))}
                transformOptions={false}
                customLabel='Difficulty'
                customPlaceholder='All difficulties'
                isClearable
                inModal
              />

              <SelectField
                name='optionality'
                options={optionalityOptions}
                transformOptions={false}
                customLabel='Optionality'
                customPlaceholder='Any'
                inModal
              />
            </FormGroup>

            <CheckboxField
              name='applyToAllTeams'
              layout='inline'
              customDescription=''
              customLabel='Apply to all my teams'
              onChange={(value) => {
                if (value === true) {
                  form.setValue('autoSignupOnly', true, {
                    shouldValidate: true,
                  })
                }
              }}
            />

            <CheckboxField
              name='autoSignupOnly'
              layout='inline'
              customDescription=''
              customLabel='Only update events I am automatically signed up for'
              disabled={applyToAllTeams}
              onChange={(value) => {
                if (applyToAllTeams && value !== true) return
                const checked = value === true
                if (
                  !checked &&
                  !form.getValues('characterId') &&
                  currentCharacterId
                ) {
                  form.setValue('characterId', currentCharacterId.toString(), {
                    shouldValidate: true,
                  })
                  const currentCharacter = selectableCharacters.find(
                    (character) => character.id === currentCharacterId,
                  )
                  const selectableRoles = currentCharacter
                    ? getSelectableRolesForCharacter(currentCharacter.classId)
                    : []
                  form.setValue('role', selectableRoles[0] ?? null, {
                    shouldValidate: true,
                  })
                }
              }}
            />

            <FormGroup>
              <SelectField
                name='characterId'
                options={selectableCharacters.map((character) => ({
                  value: character.id.toString(),
                  label: (
                    <TeamMemberLink
                      member={teamMembershipByCharacterId.get(character.id)}
                      characterReference={character}
                      disableNavigation
                    />
                  ),
                }))}
                transformOptions={false}
                customLabel={`Character`}
                customPlaceholder='Any character'
                required={!autoSignupOnly}
                inModal
                isClearable={autoSignupOnly}
                onChange={(value) => {
                  const nextCharacterId = value ? Number(value) : undefined
                  const nextCharacter = nextCharacterId
                    ? selectableCharacters.find(
                        (character) => character.id === nextCharacterId,
                      )
                    : undefined
                  const selectableRoles = nextCharacter
                    ? getSelectableRolesForCharacter(nextCharacter.classId)
                    : []
                  form.setValue('role', selectableRoles[0] ?? null, {
                    shouldValidate: true,
                  })
                }}
              />

              {!autoSignupOnly ? (
                <SelectField
                  name='role'
                  options={availableRoles.map((value) => ({
                    value,
                    label: (
                      <span className='flex items-center gap-1.5'>
                        <CharacterRoleIcon role={value} size='small' />
                        <span className='capitalize'>{value}</span>
                      </span>
                    ),
                  }))}
                  transformOptions={false}
                  required
                  customLabel='Role'
                  customPlaceholder='Select role'
                  inModal
                />
              ) : null}
            </FormGroup>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}

const BulkSetStatusDialogSubmitButtons = ({
  dayEvents,
  availableStatuses,
  onCancel,
  isBulkUpdating,
}: {
  dayEvents: wowaudit.EventReference[]
  availableStatuses: EventParticipantStatus[]
  onCancel: () => void
  isBulkUpdating: boolean
}) => {
  const form = useFormContext<BulkSetStatusFormValues>()
  const values = form.watch()
  const previewEvents = useBulkStatusPreviewEvents(dayEvents, values)
  const eventLabel = previewEvents.length === 1 ? 'event' : 'events'
  const applyToAllTeamsSuffix = values.applyToAllTeams ? ' (in this team)' : ''

  return (
    <div className='flex w-full items-center justify-between gap-4'>
      <Card className='py-2 border-dashed bg-card/50 hover:bg-card/50'>
        <span
          className={cn(
            'font-medium',
            previewEvents.length > 0
              ? 'text-success-foreground'
              : 'text-warning-foreground',
          )}
        >
          {previewEvents.length}
        </span>{' '}
        {eventLabel} will be updated
        {applyToAllTeamsSuffix}
      </Card>

      <div className='flex items-center gap-4'>
        <Button type='button' variant='outline' size='sm' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type='submit'
          size='sm'
          loading={isBulkUpdating}
          disabled={isBulkUpdating || !availableStatuses.length}
        >
          Save
        </Button>
      </div>
    </div>
  )
}

const BulkSetStatusDialog = ({ open, onOpenChange }: Props) => {
  const { team } = useTeam()
  const { currentCharacterRef } = useContext(AppContext)
  const {
    data: { user },
  } = usePageInfo()
  const { bulkUpdateStatusAsync, isBulkUpdating } =
    useBulkUserEventStatusMutation()

  const { data: futureTeamEvents = [] } = useQuery({
    queryKey: [
      'teamEvents',
      { id: team.id, type: 'bulk-status-future-events' },
    ],
    queryFn: async () => {
      if (!team.id) return []
      const response = await getTeamOverviewEvents(team.id, { limit: 100 })

      return response.events.filter(
        (event) => !dayjs(event.date).isBefore(dayjs(), 'day'),
      )
    },
    enabled: open && !!team.id,
    staleTime: 0,
  })

  const availableStatuses = useMemo(
    () =>
      (team.settings.raidsAvailableStatuses.value ??
        []) as EventParticipantStatus[],
    [team.settings.raidsAvailableStatuses.value],
  )

  const teamCharacterIds = useMemo(
    () =>
      new Set(
        (team.members ?? [])
          .filter((member) => member.characterReference.userId === user?.id)
          .map((member) => member.characterReference.id),
      ),
    [team.members, user?.id],
  )

  const selectableCharacters = useMemo(() => {
    const userCharacters = user?.characterReferences ?? []
    const eligibleCharacters = userCharacters.filter((character) =>
      teamCharacterIds.has(character.id),
    )
    if (
      currentCharacterRef &&
      eligibleCharacters.every(
        (character) => character.id !== currentCharacterRef.id,
      )
    ) {
      return [currentCharacterRef, ...eligibleCharacters]
    }
    return eligibleCharacters
  }, [currentCharacterRef, teamCharacterIds, user?.characterReferences])
  const teamMembershipByCharacterId = useMemo(
    () =>
      new Map(
        (team.members ?? [])
          .filter((member) => member.characterReference.userId === user?.id)
          .map((member) => [member.characterReference.id, member] as const),
      ),
    [team.members, user?.id],
  )

  const formSchema = useMemo(
    () => getBulkSetStatusFormSchema(availableStatuses),
    [availableStatuses],
  )
  const formDefaults = useMemo<BulkSetStatusFormValues>(
    () => ({
      dateRange: undefined,
      weekDay: null,
      status: availableStatuses[0] ?? '',
      comment: '',
      difficulty: null,
      optionality: 'any',
      applyToAllTeams: false,
      characterId: null,
      autoSignupOnly: true,
      role: null,
    }),
    [availableStatuses],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[620px]'>
        <DialogHeader>
          <DialogTitle>Bulk set status</DialogTitle>
        </DialogHeader>

        {!open ? null : (
          <FormWrapper<BulkSetStatusFormEntity>
            key={`bulk-status-${team.id}-${availableStatuses.join('-')}`}
            formSchema={formSchema}
            entityType='event'
            parentEntityType='team'
            parentId={team.id}
            entity={formDefaults}
            showSuccessToast={false}
            customSubmit={async (_form, rawValues) => {
              const values = rawValues as BulkSetStatusFormValues
              if (!values.dateRange?.from || !values.dateRange.to) return

              const optionalValue =
                values.optionality === 'any'
                  ? null
                  : values.optionality === 'only_optional'
                  ? true
                  : false

              await bulkUpdateStatusAsync({
                teamId: values.applyToAllTeams ? null : team.id,
                fromDate: dayjs(values.dateRange.from).format('YYYY-MM-DD'),
                untilDate: dayjs(values.dateRange.to).format('YYYY-MM-DD'),
                weekDay: values.weekDay ? Number(values.weekDay) : null,
                characterId: values.characterId
                  ? Number(values.characterId)
                  : null,
                role: values.role ?? null,
                status: values.status as EventParticipantStatus,
                difficulty: values.difficulty ?? null,
                optional: optionalValue,
                comment: values.comment.trim() ? values.comment : null,
                autoSignupOnly: values.autoSignupOnly,
              })

              onOpenChange(false)
            }}
            submitButtons={({ isPending }) => (
              <BulkSetStatusDialogSubmitButtons
                dayEvents={futureTeamEvents}
                availableStatuses={availableStatuses}
                onCancel={() => onOpenChange(false)}
                isBulkUpdating={isBulkUpdating || isPending}
              />
            )}
          >
            <BulkSetStatusDialogFields
              availableStatuses={availableStatuses}
              selectableCharacters={selectableCharacters}
              teamMembershipByCharacterId={teamMembershipByCharacterId}
              currentCharacterId={currentCharacterRef?.id}
              dayEvents={futureTeamEvents}
            />
          </FormWrapper>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default BulkSetStatusDialog
